import React, { useRef, useState } from 'react';
import { Input } from 'reactstrap';
import './LoiAndTermsForm.scss';
import { TermsFormDisplay } from './TermsFormDisplay';
import { LoiForm, SellerLocation } from '../../../../../../slices/marketplace/loi-form/loi-form';
import { TermForm, TermFormLocation, createEmptyTermForm } from '../../../../../../slices/marketplace/loi-form/term-form';
import { useStateSelector } from '../../../../../../store/selectors';
import createNotification from '../../../../../../utils/createNotification';
import ButtonLoader from '../../../../../../components/Layout/Buttons/ButtonLoader';
import { TermsFormSelect } from './TermsFormSelect';
import { TimelineActionType } from '../../../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { LoiFormAnswerType, LoiFormAnswerTypeList } from '../../../../../../slices/marketplace/loi-form/loi-form-answer-type.enum';

const YES_NO_OPTIONS = [{ value: 'Yes' }, { value: 'No' }] as const;

const DEFAULT_LEASE_TYPE = LoiFormAnswerTypeList.find(f => f.id === LoiFormAnswerType.LeaseType).options[0].value;

const LEASE_ASSUMPTION_OPTIONS = [
    { value: 'existing', label: 'Assume an Existing Lease' },
    { value: 'new', label: 'Assume a New Lease' },
] as const;

function booleanToYesNo(value: boolean | null | undefined): string {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '';
}

function yesNoToBoolean(value: string | null): boolean | null {
    if (value === 'Yes') return true;
    if (value === 'No') return false;
    return null;
}

interface LoiAndTermsFormProps {
    formData: LoiForm;
    isReadonly: boolean;
    onChange: (termForm: TermForm) => void;
    errors?: Set<string>;
    opportunityId?: number;
    loiActionId?: number;
    uploadedDocumentName?: string;
    documentDetailsId?: string;
    onUploadComplete?: () => void;
    loiFileError?: boolean;
    actionType?: TimelineActionType;
    isFsb?: boolean;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
    sellerLocations: SellerLocation[];
}

export const LoiAndTermsForm = (props: LoiAndTermsFormProps) => {
    const termForm: TermForm = props.formData.termForm ?? createEmptyTermForm();
    const axios = useStateSelector((state) => state.core.axios);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [localUploadedFileName, setLocalUploadedFileName] = useState<string | undefined>(undefined);

    const uploadedFileName = localUploadedFileName ?? props.uploadedDocumentName;

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !axios || !props.opportunityId || !props.loiActionId) {
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            const response = await axios.post(
                `/api/Marketplace/${props.opportunityId}/MarketplaceOpportunityDocuments/UploadActionDocument/${props.loiActionId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            if (response.status !== 200) {
                const message = (response as any)?.response?.data?.detail;
                createNotification(
                    message ?? 'An error occurred while uploading the file',
                    'error'
                );
            } else {
                setLocalUploadedFileName(file.name);
                createNotification('File uploaded successfully', 'success');
                props.onUploadComplete?.();
            }
        } catch {
            createNotification('An error occurred while uploading the file', 'error');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const canUpload = !props.isReadonly && !!props.opportunityId && !!props.loiActionId;

    const handleOpenDocument = () => {
        if (!props.documentDetailsId || !props.opportunityId || !axios) return;
        axios
            .get(
                `/api/marketplace/${props.opportunityId}/MarketplaceOpportunityDocuments/${props.documentDetailsId}`
            )
            .then((response) => {
                if (response.status === 200) {
                    window.open(response.data, '_blank');
                }
            })
            .catch(() =>
                createNotification(
                    'An error occurred while opening the file.',
                    'error'
                )
            );
    };

    const isDocumentClickable = !!props.documentDetailsId && !!props.opportunityId;

    return (
        <div className="loi-and-terms-form">
            <div className={`loi-document-upload${props.isReadonly ? ' loi-document-upload--readonly' : ''}`}>
                <div className="loi-document-upload-row">
                    <div className="loi-document-upload-label">{props.isReadonly ? 'LOI File' : 'LOI File Upload'}</div>
                    {canUpload && (
                        <>
                            <ButtonLoader
                                buttonText="Upload File"
                                loaderButtonText=""
                                isLoading={isUploading}
                                disabled={isUploading}
                                onClick={handleUploadClick}
                                className="btn btn-primary"
                            />
                            <input
                                hidden
                                ref={fileInputRef}
                                type="file"
                                name="loi-document"
                                accept=".pdf"
                                onChange={handleFileChange}
                            />
                        </>
                    )}
                    {uploadedFileName && isDocumentClickable && (
                        <div
                            className="loi-document-upload-filename loi-document-upload-filename--clickable"
                            onClick={handleOpenDocument}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleOpenDocument();
                                }
                            }}
                            role="button"
                            tabIndex={0}>
                            {uploadedFileName}
                        </div>
                    )}
                </div>
                {props.loiFileError && (
                    <div className="loi-document-upload-error text-danger">LOI file is required</div>
                )}
            </div>
            <div className="loi-preliminary-questions">
                <div className="loi-preliminary-questions-row">
                    <div className="loi-preliminary-questions-field">
                        <span className="loi-preliminary-questions-label">Are you expecting one or more of the owners to be employed post-close?</span>
                        <div className="loi-preliminary-questions-value">
                            <TermsFormSelect
                                value={booleanToYesNo(termForm.isOwnerEmploymentExpected)}
                                options={YES_NO_OPTIONS}
                                disabled={props.isReadonly}
                                onChange={(value) => props.onChange({ ...termForm, isOwnerEmploymentExpected: yesNoToBoolean(value) })}
                                aria-label="Are you expecting one or more of the owners to be employed post-close?"
                                invalid={props.errors?.has('isOwnerEmploymentExpected')}
                            />
                        </div>
                    </div>
                    {props.sellerLocations.length === 1 && (
                        <div className="loi-preliminary-questions-field">
                            <span className="loi-preliminary-questions-label">Are you expecting to assume an existing lease or assume a new one?</span>
                            <div className="loi-preliminary-questions-value">
                                <TermsFormSelect
                                    value={termForm.locations?.[0]?.leaseAssumption ?? ''}
                                    options={LEASE_ASSUMPTION_OPTIONS}
                                    className="loi-lease-assumption-select"
                                    disabled={props.isReadonly}
                                    onChange={(value) => {
                                        const leaseAssumption = value as TermFormLocation['leaseAssumption'];
                                        const location: TermFormLocation = {
                                            locationId: props.sellerLocations[0].locationId,
                                            locationName: props.sellerLocations[0].locationName,
                                            leaseAssumption,
                                            leaseType: leaseAssumption === 'new' ? DEFAULT_LEASE_TYPE : null,
                                        };
                                        props.onChange({ ...termForm, locations: [location] });
                                    }}
                                    aria-label="Are you expecting to assume an existing lease or assume a new one?"
                                    invalid={props.errors?.has('locations')}
                                />
                            </div>
                        </div>
                    )}
                    {props.actionType !== TimelineActionType.BuyerLoiSubmission && (
                        <div className="loi-preliminary-questions-field">
                            <span className="loi-preliminary-questions-label">Is there any consideration paid by the buyer or the seller intended to go to practice employees?</span>
                            <div className="loi-preliminary-questions-value">
                                <TermsFormSelect
                                    value={booleanToYesNo(termForm.isEmployeeConsiderationPresent)}
                                    options={YES_NO_OPTIONS}
                                    disabled={props.isReadonly}
                                    onChange={(value) => props.onChange({ ...termForm, isEmployeeConsiderationPresent: yesNoToBoolean(value) })}
                                    aria-label="Is there any consideration paid by the buyer or the seller intended to go to practice employees?"
                                    invalid={props.errors?.has('isEmployeeConsiderationPresent')}
                                />
                            </div>
                        </div>
                    )}
                </div>
                {props.sellerLocations.length > 1 && (
                    <div>
                        <table className="loi-lease-assumption-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Assume Existing Lease</th>
                                    <th>Assume New Lease</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.sellerLocations.map((loc) => {
                                    const selectedValue =
                                        termForm.locations?.find((l) => l.locationId === loc.locationId)
                                            ?.leaseAssumption ?? null;
                                    const handleLocationChange = (
                                        leaseAssumption: TermFormLocation['leaseAssumption'],
                                    ) => {
                                        const existing = termForm.locations ?? [];
                                        const leaseFieldsClear: Partial<TermFormLocation> = leaseAssumption !== 'new'
                                            ? { termOfLease: null, leaseType: null, leaseRate: null, leaseRateEscalation: null, leaseRateUponExtension: null }
                                            : { leaseType: DEFAULT_LEASE_TYPE };
                                        const updated = existing.some((l) => l.locationId === loc.locationId)
                                            ? existing.map((l) =>
                                                l.locationId === loc.locationId ? { ...l, leaseAssumption, ...leaseFieldsClear } : l,
                                            )
                                            : [
                                                ...existing,
                                                {
                                                    locationId: loc.locationId,
                                                    locationName: loc.locationName,
                                                    leaseAssumption,
                                                    ...leaseFieldsClear,
                                                },
                                            ];
                                        props.onChange({ ...termForm, locations: updated });
                                    };
                                    return (
                                        <tr key={loc.locationId}>
                                            <td className="loi-lease-assumption-table__location-name">
                                                {loc.locationName}
                                            </td>
                                            <td className="loi-lease-assumption-table__radio-cell">
                                                <div className="custom-control-radio">
                                                    <input
                                                        type="radio"
                                                        id={`lease-assumption-${loc.locationId}-existing`}
                                                        name={`lease-assumption-${loc.locationId}`}
                                                        value="existing"
                                                        checked={selectedValue === 'existing'}
                                                        disabled={props.isReadonly}
                                                        onChange={() => handleLocationChange('existing')}
                                                        className="form-check-input"
                                                    />
                                                    <label
                                                        className="label-text"
                                                        htmlFor={`lease-assumption-${loc.locationId}-existing`}
                                                    />
                                                    <label
                                                        htmlFor={`lease-assumption-${loc.locationId}-existing`}
                                                        className={`bullet${selectedValue === null && props.errors?.has('locations') ? ' bullet--invalid' : ''}`}>
                                                        <span className="bullet-selected"></span>
                                                    </label>
                                                </div>
                                            </td>
                                            <td className="loi-lease-assumption-table__radio-cell">
                                                <div className="custom-control-radio">
                                                    <input
                                                        type="radio"
                                                        id={`lease-assumption-${loc.locationId}-new`}
                                                        name={`lease-assumption-${loc.locationId}`}
                                                        value="new"
                                                        checked={selectedValue === 'new'}
                                                        disabled={props.isReadonly}
                                                        onChange={() => handleLocationChange('new')}
                                                        className="form-check-input"
                                                    />
                                                    <label
                                                        className="label-text"
                                                        htmlFor={`lease-assumption-${loc.locationId}-new`}
                                                    />
                                                    <label
                                                        htmlFor={`lease-assumption-${loc.locationId}-new`}
                                                        className={`bullet${selectedValue === null && props.errors?.has('locations') ? ' bullet--invalid' : ''}`}>
                                                        <span className="bullet-selected"></span>
                                                    </label>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="loi-preliminary-questions-field loi-preliminary-questions-field--standalone">
                    <span className="loi-preliminary-questions-label">Please list any vendor contracts you expect to assume</span>
                    <div className="loi-preliminary-questions-value">
                        <Input
                            type="textarea"
                            rows={4}
                            value={termForm.vendorContracts ?? ''}
                            disabled={props.isReadonly}
                            onChange={(e) => props.onChange({ ...termForm, vendorContracts: e.target.value || null })}
                            aria-label="Please list any vendor contracts you expect to assume"
                        />
                    </div>
                </div>
            </div>
            {props.actionType !== TimelineActionType.BuyerLoiSubmission && (
                <div className="loi-and-terms-form-content">
                    <TermsFormDisplay
                        termForm={termForm}
                        legalIssues={props.formData.legalIssues ?? []}
                        additionalCovenants={props.formData.additionalCovenants ?? []}
                        readOnly={props.isReadonly}
                        onChange={(updated) => props.onChange(updated)}
                        errors={props.errors}
                        isFsb={props.isFsb}
                        isBuyerReview={props.isBuyerReview}
                        isBuyerReviewConfirmed={props.isBuyerReviewConfirmed}
                        fieldComments={props.formData?.termForm?.fieldComments}
                        onFieldCommentChange={props.onFieldCommentChange}
                    />
                </div>
            )}
        </div>
    );
};
